import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";

export const Route = createFileRoute("/pay/$token")({
  component: PayPage,
  head: () => ({ meta: [{ title: "Pagar — Structura Market" }] }),
});

interface PayData {
  request: { id: string; amountCents: number; currency: string; description: string | null; status: string };
  tenantName: string;
  tenantLogoUrl?: string | null;
  payway: { configured: boolean; environment?: string; publicKey?: string; apiBaseUrl?: string };
  paywayTenantEnabled?: boolean;
  redirectProviders?: { id: string; label: string }[];
  preferredGateway?: string | null;
  gatewayLocked?: boolean;
}

type Phase = "loading" | "form" | "processing" | "approved" | "rejected" | "expired" | "error" | "paid" | "modo_qr";

interface ModoCheckout {
  qrSvg?: string;
  deepLink?: string;
  checkoutUrl?: string;
}

const PROVIDER_LABELS: Record<string, string> = {
  payway: "Payway",
  mercadopago: "Mercado Pago",
  modo: "MODO",
  getnet: "Getnet",
  uala: "Ualá Bis",
};

const REDIRECT_GATEWAYS = new Set(["mercadopago", "uala", "getnet"]);

const CHECKOUT_PATH: Record<string, string> = {
  mercadopago: "mp",
  uala: "uala",
  modo: "modo",
  getnet: "getnet",
};

const INPUT =
  "w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-base text-gray-900 placeholder:text-gray-400 shadow-sm focus:border-emerald-600 focus:outline-none focus:ring-2 focus:ring-emerald-500/25";
const LABEL = "mb-1 block text-sm font-medium text-gray-700";

function fmt(cents: number, cur: string) {
  return new Intl.NumberFormat("es-AR", { style: "currency", currency: cur || "ARS" }).format(cents / 100);
}

function PayShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-slate-900 px-4 py-10 text-gray-900">
      <div className="mx-auto max-w-md rounded-2xl bg-white p-6 shadow-xl ring-1 ring-black/5">{children}</div>
    </div>
  );
}

function PayPage() {
  const { token } = Route.useParams();
  const [data, setData] = useState<PayData | null>(null);
  const [phase, setPhase] = useState<Phase>("loading");
  const [error, setError] = useState("");
  const [auth, setAuth] = useState("");

  const [number, setNumber] = useState("");
  const [name, setName] = useState("");
  const [expM, setExpM] = useState("");
  const [expY, setExpY] = useState("");
  const [cvv, setCvv] = useState("");
  const [dni, setDni] = useState("");
  const [modoCheckout, setModoCheckout] = useState<ModoCheckout | null>(null);
  const [fallback, setFallback] = useState<{ provider: string; label: string } | null>(null);

  useEffect(() => {
    const params = typeof window !== "undefined" ? new URLSearchParams(window.location.search) : null;
    const gateway = params?.get("gateway");
    const ualaResult = params?.get("uala");

    async function loadPay() {
      const r = await fetch(`/api/pay/${token}${gateway ? `?gateway=${encodeURIComponent(gateway)}` : ""}`);
      if (!r.ok) throw new Error("No encontrada");
      const d = (await r.json()) as PayData & { status?: string };
      if (d.request.status !== "paid" && ualaResult === "success") {
        await fetch(`/api/pay/${token}/uala/sync`, { method: "POST" }).catch(() => null);
        const r2 = await fetch(`/api/pay/${token}`);
        if (r2.ok) Object.assign(d, await r2.json());
      }
      setData(d);
      if (d.request.status === "paid") setPhase("approved");
      else if (d.status === "expired") setPhase("expired");
      else setPhase("form");
    }

    void loadPay().catch(() => setPhase("error"));
  }, [token]);

  async function payRedirect(providerId: string) {
    setPhase("processing");
    setError("");
    const path = CHECKOUT_PATH[providerId] ?? providerId;
    try {
      const res = await fetch(`/api/pay/${token}/${path}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: "{}",
      });
      const d = (await res.json()) as {
        ok?: boolean;
        checkoutUrl?: string;
        initPoint?: string;
        qrSvg?: string;
        deepLink?: string;
        error?: string;
      };
      if (d.ok && providerId === "modo" && (d.qrSvg || d.deepLink)) {
        setModoCheckout({ qrSvg: d.qrSvg, deepLink: d.deepLink, checkoutUrl: d.checkoutUrl });
        setPhase("modo_qr");
        return;
      }
      const checkoutUrl = d.checkoutUrl ?? d.initPoint;
      if (d.ok && checkoutUrl) {
        window.location.href = checkoutUrl;
        return;
      }
      setError(d.error ?? "No se pudo iniciar el pago");
      setPhase("form");
    } catch {
      setError("Error de conexión");
      setPhase("form");
    }
  }

  async function pay(e: React.FormEvent) {
    e.preventDefault();
    if (!data?.payway.configured || !data.payway.publicKey || !data.payway.apiBaseUrl) {
      setError("Pagos no disponibles");
      setPhase("error");
      return;
    }
    setPhase("processing");
    setError("");
    try {
      const tokRes = await fetch(`${data.payway.apiBaseUrl}/tokens`, {
        method: "POST",
        headers: { apikey: data.payway.publicKey, "Content-Type": "application/json" },
        body: JSON.stringify({
          card_number: number.replace(/\s/g, ""),
          card_expiration_month: expM.padStart(2, "0"),
          card_expiration_year: expY.slice(-2),
          security_code: cvv,
          card_holder_name: name,
          card_holder_identification: { type: "dni", number: dni },
        }),
      });
      const tok = (await tokRes.json()) as { id?: string; bin?: string };
      if (!tokRes.ok || !tok.id) {
        setError("No pudimos validar la tarjeta. Revisá los datos.");
        setPhase("rejected");
        return;
      }
      const payRes = await fetch(`/api/pay/${token}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: tok.id, bin: tok.bin, paymentMethodId: 1 }),
      });
      const pay = (await payRes.json()) as {
        ok?: boolean;
        status?: string;
        authorizationCode?: string;
        error?: string;
        fallback?: { provider: string; label: string } | null;
      };
      if (pay.ok && pay.status === "approved") {
        setAuth(pay.authorizationCode ?? "");
        setPhase("approved");
      } else {
        setError(pay.error ?? "El pago fue rechazado");
        setFallback(pay.fallback ?? null);
        setPhase("rejected");
      }
    } catch {
      setError("Error de conexión");
      setPhase("error");
    }
  }

  const gatewayLocked = data?.gatewayLocked === true;
  const preferred = data?.preferredGateway ?? null;
  const showPaywayForm =
    Boolean(data?.payway?.configured && data?.payway?.publicKey) &&
    (preferred === "payway" || (!preferred && !gatewayLocked)) &&
    !REDIRECT_GATEWAYS.has(preferred ?? "");
  const paywayUnavailableMessage = gatewayLocked
    ? "Payway no está disponible en este momento. Contactá a la tienda para completar el pago."
    : data?.preferredGateway === "payway" || data?.paywayTenantEnabled
      ? "Payway está habilitado para este negocio, pero el servidor aún no tiene las credenciales de cobro configuradas."
      : "El cobro con tarjeta (Payway) no está disponible.";
  const gatewayHint = preferred
    ? PROVIDER_LABELS[preferred] ?? data?.redirectProviders?.find((p) => p.id === preferred)?.label ?? preferred
    : null;
  const preferredRedirect = data?.redirectProviders?.find((p) => p.id === preferred);
  const otherRedirects = gatewayLocked
    ? []
    : (data?.redirectProviders?.filter((p) => p.id !== preferred) ?? []);

  if (phase === "loading") {
    return (
      <PayShell>
        <p className="text-center text-gray-600">Cargando…</p>
      </PayShell>
    );
  }

  if (phase === "approved") {
    return (
      <PayShell>
        <div className="text-center">
          <div className="text-5xl">✅</div>
          <h1 className="mt-3 text-xl font-bold text-emerald-700">¡Pago aprobado!</h1>
          {data && <p className="mt-1 text-lg font-semibold text-gray-900">{fmt(data.request.amountCents, data.request.currency)}</p>}
          {auth && <p className="mt-2 text-sm text-gray-600">Código de autorización: {auth}</p>}
          <p className="mt-4 text-sm text-gray-600">Ya podés cerrar esta página.</p>
        </div>
      </PayShell>
    );
  }

  if (phase === "paid") {
    return (
      <PayShell>
        <div className="text-center">
          <div className="text-5xl">✅</div>
          <h1 className="mt-3 text-xl font-bold text-emerald-700">Esta solicitud ya fue pagada</h1>
        </div>
      </PayShell>
    );
  }

  if (phase === "modo_qr" && modoCheckout) {
    return (
      <PayShell>
        <div className="text-center space-y-4">
          <h1 className="text-lg font-bold text-gray-900">Pagá con MODO</h1>
          {data && (
            <p className="text-2xl font-extrabold text-gray-900">{fmt(data.request.amountCents, data.request.currency)}</p>
          )}
          {modoCheckout.qrSvg ? (
            <div
              className="mx-auto inline-block rounded-xl border p-3 bg-white"
              dangerouslySetInnerHTML={{ __html: modoCheckout.qrSvg }}
            />
          ) : null}
          <p className="text-sm text-gray-600">Escaneá el QR con la app MODO o usá el botón de abajo.</p>
          {modoCheckout.deepLink ? (
            <a
              href={modoCheckout.deepLink}
              className="block w-full rounded-lg bg-sky-600 py-3 text-base font-semibold text-white hover:bg-sky-700"
            >
              Abrir en MODO
            </a>
          ) : null}
          {modoCheckout.checkoutUrl ? (
            <a
              href={modoCheckout.checkoutUrl}
              className="block w-full rounded-lg border border-gray-300 py-2.5 text-base font-medium text-gray-900 hover:bg-gray-50"
            >
              Pagar en el navegador
            </a>
          ) : null}
          {!gatewayLocked ? (
            <button
              type="button"
              onClick={() => { setPhase("form"); setModoCheckout(null); }}
              className="text-sm text-gray-500 underline"
            >
              Elegir otro medio de pago
            </button>
          ) : null}
        </div>
      </PayShell>
    );
  }

  if (phase === "expired") {
    return (
      <PayShell>
        <div className="text-center">
          <div className="text-5xl">⌛</div>
          <h1 className="mt-3 text-xl font-bold text-amber-600">El link de pago expiró</h1>
        </div>
      </PayShell>
    );
  }

  if (phase === "error") {
    return (
      <PayShell>
        <div className="text-center">
          <div className="text-5xl">⚠️</div>
          <h1 className="mt-3 text-xl font-bold text-gray-800">No pudimos cargar el pago</h1>
          <p className="mt-2 text-sm text-gray-600">{error}</p>
        </div>
      </PayShell>
    );
  }

  return (
    <PayShell>
      <div className="mb-5 text-center">
        {data?.tenantLogoUrl ? (
          <img
            src={data.tenantLogoUrl}
            alt={data.tenantName}
            className="mx-auto mb-3 h-14 max-w-[220px] object-contain"
          />
        ) : (
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">{data?.tenantName}</p>
        )}
        <h1 className="text-lg font-bold text-gray-900">{data?.request.description ?? "Pago"}</h1>
        {data && (
          <p className="mt-1 text-2xl font-extrabold text-gray-900">{fmt(data.request.amountCents, data.request.currency)}</p>
        )}
        {gatewayHint && (
          <p className="mt-2 text-sm text-gray-600">
            Medio de cobro: <span className="font-medium text-gray-900">{gatewayHint}</span>
          </p>
        )}
        {data?.payway.environment === "sandbox" && (
          <p className="mt-2 rounded-md bg-amber-50 px-2 py-1 text-[11px] text-amber-800">
            Modo prueba — tarjeta 4507 9900 0000 4905, 12/50, CVV 123
          </p>
        )}
      </div>

      {showPaywayForm ? (
        <form onSubmit={pay} className="space-y-4">
          <div>
            <label className={LABEL}>Número de tarjeta</label>
            <input
              required
              value={number}
              onChange={(e) => setNumber(e.target.value)}
              placeholder="1234 5678 9012 3456"
              inputMode="numeric"
              autoComplete="cc-number"
              className={INPUT}
            />
          </div>
          <div>
            <label className={LABEL}>Nombre en la tarjeta</label>
            <input
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Como figura en la tarjeta"
              autoComplete="cc-name"
              className={INPUT}
            />
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <label className={LABEL}>Mes</label>
              <input
                required
                value={expM}
                onChange={(e) => setExpM(e.target.value)}
                placeholder="MM"
                inputMode="numeric"
                maxLength={2}
                autoComplete="cc-exp-month"
                className={INPUT}
              />
            </div>
            <div>
              <label className={LABEL}>Año</label>
              <input
                required
                value={expY}
                onChange={(e) => setExpY(e.target.value)}
                placeholder="AA"
                inputMode="numeric"
                maxLength={4}
                autoComplete="cc-exp-year"
                className={INPUT}
              />
            </div>
            <div>
              <label className={LABEL}>CVV</label>
              <input
                required
                value={cvv}
                onChange={(e) => setCvv(e.target.value)}
                placeholder="123"
                inputMode="numeric"
                maxLength={4}
                autoComplete="cc-csc"
                className={INPUT}
              />
            </div>
          </div>
          <div>
            <label className={LABEL}>DNI del titular</label>
            <input
              required
              value={dni}
              onChange={(e) => setDni(e.target.value)}
              placeholder="12345678"
              inputMode="numeric"
              className={INPUT}
            />
          </div>
          {phase === "rejected" && error ? (
            <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>
          ) : null}
          {phase === "rejected" && fallback ? (
            <button
              type="button"
              disabled={phase !== "rejected"}
              onClick={() => void payRedirect(fallback.provider)}
              className="w-full rounded-lg border border-gray-300 bg-white py-2.5 text-base font-medium text-gray-900 hover:bg-gray-50"
            >
              Pagar con {fallback.label}
            </button>
          ) : null}
          <button
            type="submit"
            disabled={phase === "processing"}
            className="w-full rounded-lg bg-emerald-600 py-3 text-base font-semibold text-white hover:bg-emerald-700 disabled:opacity-60"
          >
            {phase === "processing" ? "Procesando…" : `Pagar ${data ? fmt(data.request.amountCents, data.request.currency) : ""}`}
          </button>
          <p className="text-center text-xs text-gray-500">Pago seguro. Tus datos de tarjeta no se almacenan.</p>
        </form>
      ) : preferredRedirect ? (
        <div className="space-y-3">
          <button
            type="button"
            disabled={phase === "processing"}
            onClick={() => void payRedirect(preferredRedirect.id)}
            className="w-full rounded-lg bg-sky-600 py-3 text-base font-semibold text-white hover:bg-sky-700 disabled:opacity-60"
          >
            {phase === "processing" ? "Redirigiendo…" : `Pagar con ${preferredRedirect.label}`}
          </button>
          {error ? <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p> : null}
        </div>
      ) : (
        <p className="rounded-lg bg-gray-50 px-3 py-2 text-sm text-gray-700">
          {paywayUnavailableMessage}
        </p>
      )}

      { !gatewayLocked && otherRedirects.length > 0 ? (
        <div className="mt-6 space-y-2 border-t border-gray-200 pt-5">
          <p className="text-center text-sm font-medium text-gray-600">O pagá con</p>
          {otherRedirects.map((p) => (
            <button
              key={p.id}
              type="button"
              disabled={phase === "processing"}
              onClick={() => void payRedirect(p.id)}
              className="w-full rounded-lg border border-gray-300 bg-white py-2.5 text-base font-medium text-gray-900 hover:bg-gray-50 disabled:opacity-60"
            >
              {p.label}
            </button>
          ))}
          {error && !preferredRedirect ? (
            <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>
          ) : null}
        </div>
      ) : !gatewayLocked && !preferredRedirect && data?.redirectProviders && data.redirectProviders.length > 0 ? (
        <div className="mt-6 space-y-2 border-t border-gray-200 pt-5">
          <p className="text-center text-sm font-medium text-gray-600">O pagá con</p>
          {data.redirectProviders.map((p) => (
            <button
              key={p.id}
              type="button"
              disabled={phase === "processing"}
              onClick={() => void payRedirect(p.id)}
              className="w-full rounded-lg border border-gray-300 bg-white py-2.5 text-base font-medium text-gray-900 hover:bg-gray-50 disabled:opacity-60"
            >
              {p.label}
            </button>
          ))}
          {error ? <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p> : null}
        </div>
      ) : null}
    </PayShell>
  );
}
